import React, { useMemo } from 'react';
import type { PreProjetoDTO } from '../services/preProjetoService';
import { areaDe, filtrosVazios, temFiltroAtivo, type FiltrosPreProjeto } from '../utils/preProjetoFiltros';

const selectClass = 'rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white';

interface Props {
  /** Todos os itens carregados: as opções saem daqui, não da lista já filtrada. */
  items: PreProjetoDTO[];
  value: FiltrosPreProjeto;
  onChange: (value: FiltrosPreProjeto) => void;
  /** Mostra também o filtro de instituição, limitado às instituições da área escolhida. */
  comInstituicao?: boolean;
  /** Mostra os filtros de situação (disponível/escolhido) e de quem escolheu o pré-projeto. */
  comSelecao?: boolean;
}

/** Filtros por área de atuação, exercício e (opcional) instituição das telas de pré-projetos. */
const PreProjetoFiltros: React.FC<Props> = ({ items, value, onChange, comInstituicao = false, comSelecao = false }) => {
  const selecionadores = useMemo(() => {
    const porId = new Map<string, string>();
    items.forEach((item) => {
      if (item.etapa === 'SELECIONADO' && item.selecionadoPorUsuarioId != null) {
        porId.set(String(item.selecionadoPorUsuarioId), item.selecionadoPor || 'Usuário não identificado');
      }
    });
    return Array.from(porId.entries()).sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'));
  }, [items]);
  const areas = useMemo(
    () => Array.from(new Set(items.map(areaDe))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [items],
  );
  const exercicios = useMemo(
    () => Array.from(new Set(items.map((item) => item.exercicio))).sort((a, b) => b - a),
    [items],
  );
  // Cascata: escolhida a área, só aparecem as instituições que têm projeto nela.
  const instituicoes = useMemo(() => {
    const porId = new Map<string, string>();
    items
      .filter((item) => !value.area || areaDe(item) === value.area)
      .forEach((item) => porId.set(item.instituicaoId, item.instituicaoNome));
    return Array.from(porId.entries()).sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'));
  }, [items, value.area]);

  const temFiltro = temFiltroAtivo(value);

  return (
    <>
      <select
        aria-label="Filtrar por área de atuação"
        value={value.area}
        onChange={(e) => onChange({ ...value, area: e.target.value, instituicaoId: '' })}
        className={selectClass}
      >
        <option value="">Todas as áreas</option>
        {areas.map((area) => <option key={area} value={area}>{area}</option>)}
      </select>
      <select
        aria-label="Filtrar por exercício"
        value={value.exercicio}
        onChange={(e) => onChange({ ...value, exercicio: e.target.value })}
        className={selectClass}
      >
        <option value="">Todos os exercícios</option>
        {exercicios.map((ano) => <option key={ano} value={String(ano)}>{ano}</option>)}
      </select>
      {comInstituicao && (
        <select
          aria-label="Filtrar por instituição"
          value={value.instituicaoId}
          onChange={(e) => onChange({ ...value, instituicaoId: e.target.value })}
          className={selectClass}
        >
          <option value="">Todas as instituições</option>
          {instituicoes.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
        </select>
      )}
      {comSelecao && (
        <>
          <select
            aria-label="Filtrar por situação"
            value={value.situacao}
            onChange={(e) => onChange({ ...value, situacao: e.target.value as FiltrosPreProjeto['situacao'] })}
            className={selectClass}
          >
            <option value="">Todas as situações</option>
            <option value="DISPONIVEL">Disponíveis</option>
            <option value="ESCOLHIDO">Já escolhidos</option>
          </select>
          <select
            aria-label="Filtrar por quem escolheu"
            value={value.selecionadoPorId}
            onChange={(e) => onChange({ ...value, selecionadoPorId: e.target.value })}
            className={selectClass}
          >
            <option value="">Escolhido por (todos)</option>
            {selecionadores.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
          </select>
        </>
      )}
      {temFiltro && (
        <button
          type="button"
          onClick={() => onChange(filtrosVazios)}
          className="whitespace-nowrap text-sm font-semibold text-teal-700 hover:text-teal-800 dark:text-teal-300"
        >
          Limpar filtros
        </button>
      )}
    </>
  );
};

export default PreProjetoFiltros;
